import { SeriesCard } from 'src/component/ui/series-card'
import { SeriesCardDismissSeriesButton } from 'src/component/ui/series-card/series-card-dismiss-series-button'
import { SuggestedTitle } from 'src/component/lets-get-moving/suggested-title'
import { SUGGESTED_SERIES_ASPECT_CLASS } from 'src/constants/suggested-series'
import { Programme } from 'src/dao/core-dao'
import React, { MouseEvent } from 'react'
import { cn } from 'src/lib/utils'

export interface SuggestedSeriesCardProps {
  series: Programme
  /** Opens series preview (homepage suggested path). */
  onPreview: () => void
  className?: string
  'data-testid'?: string
  /** When true, show “Suggested” ribbon and optional dismiss control. */
  isActiveSuggested: boolean
  onDismissClick?: (e: MouseEvent<HTMLButtonElement>) => void
  nextWorkoutTitle: string
  completedCount: number
  totalCount: number | undefined
  showProgress: boolean
  isProgressReady: boolean
  /** Disables the dismiss control while a dismiss request is in flight. */
  isDismissPending?: boolean
}

const SuggestedSeriesCardSuggestedBadge = (): React.ReactElement => (
  <div className="inline-flex shrink-0 rounded-md bg-primary px-4 md:px-5 py-2 md:py-3">
    <span className="font-evogria text-base md:text-2xl text-primary-gray uppercase leading-tight">Suggested</span>
  </div>
)

export const SuggestedSeriesCard = ({
  series,
  onPreview,
  className: outerClassName,
  'data-testid': dataTestId,
  isActiveSuggested,
  onDismissClick,
  nextWorkoutTitle,
  completedCount,
  totalCount,
  showProgress,
  isProgressReady,
  isDismissPending,
}: SuggestedSeriesCardProps): React.ReactElement => {
  return (
    <div
      className={cn(`relative w-full rounded-md ${SUGGESTED_SERIES_ASPECT_CLASS} overflow-hidden shadow-lg`, outerClassName)}
      data-testid={dataTestId}
    >
      <SeriesCard
        series={series}
        className={`rounded-md ${SUGGESTED_SERIES_ASPECT_CLASS} w-full`}
        onAction={onPreview}
        actionAriaLabel={`Open series preview for ${series.title}`}
      >
        <SeriesCard.Image>
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-black/30 from-0% via-black/25 via-33% to-black/70 to-100% shadow-[inset_4px_4px_30px_0_rgb(242_241_236_/_0.2)]"
            />
            <SeriesCard.Content className="relative z-10 gap-0 !p-6">
              {isActiveSuggested ? (
                <div className="flex shrink-0 items-start justify-between gap-2">
                  <SuggestedSeriesCardSuggestedBadge />
                  <div className="flex min-w-0 flex-1 items-start justify-end gap-2">
                    {onDismissClick ? (
                      <SeriesCardDismissSeriesButton
                        ariaLabel="Dismiss series suggestion"
                        disabled={isDismissPending}
                        onDismissClick={onDismissClick}
                      />
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className="flex min-h-0 flex-1 flex-col justify-end">
                <p className="mb-1 font-roboto-condensed-medium text-lg capitalize text-ivory lg:text-xl">
                  {series.title.toLocaleLowerCase()}
                </p>
                <SuggestedTitle>{nextWorkoutTitle}</SuggestedTitle>
                {series.duration ? <SeriesCard.MetaActive /> : null}
              </div>
              {showProgress ? (
                <SeriesCard.Progress completedCount={completedCount} totalCount={totalCount} isReady={isProgressReady} />
              ) : null}
            </SeriesCard.Content>
          </>
        </SeriesCard.Image>
      </SeriesCard>
    </div>
  )
}
